import React, { useState } from 'react';
import { useProjectStore } from '../../store/projectStore';
import { FileCheck, Building, Wifi, Cable, Server, Users, MapPin, Phone, Mail, Calendar, CheckCircle, Clock, DollarSign } from 'lucide-react';
import DesignSelector from '../Designer/DesignSelector';
import { formatCurrencyNoDecimals } from '../../utils/currencyUtils';

const ProposalView = () => {
  const { currentProject, updateProjectCustomer, currency, getCurrentDesign } = useProjectStore();
  const design = getCurrentDesign();
  const [customer, setCustomer] = useState(currentProject?.customer || {
    name: '',
    address: '',
    email: '',
    phone: ''
  });

  const handleCustomerChange = (field: string, value: string) => {
    const updatedCustomer = { ...customer, [field]: value };
    setCustomer(updatedCustomer);
    updateProjectCustomer(updatedCustomer);
  };

  // Calculate totals - must be done unconditionally after hooks
  const totalEquipment = design ? (
    design.equipment.length + 
    design.racks.reduce((total, rack) => total + rack.equipment.length, 0)
  ) : 0;
  
  const totalCables = design?.cables.length || 0;

  if (!currentProject) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FileCheck size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Collection Selected</h2>
          <p className="text-gray-500">Please create or select a collection to view the proposal</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-gray-50">
      {/* Proposal Controls Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
        {/* Design Selector */}
        <DesignSelector />

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Proposal Info</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div>
              <span className="font-medium">Design:</span>
              <p className="text-gray-900">{currentProject?.name || 'Not selected'}</p>
            </div>
            <div>
              <span className="font-medium">Customer:</span>
              <p className="text-gray-900">{currentProject?.customer.name || 'Not selected'}</p>
            </div>
            <div>
              <span className="font-medium">Equipment:</span>
              <p className="text-gray-900">{design?.equipment.length || 0} items</p>
            </div>
            <div>
              <span className="font-medium">Cables:</span>
              <p className="text-gray-900">{design?.cables.length || 0} runs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Proposal Content */}
      <div className="flex-1 p-6 overflow-y-auto paper-preview-light">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileCheck className="text-blue-600" size={32} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Network Infrastructure Proposal</h1>
                <p className="text-lg text-gray-600">Professional Network Design & Implementation</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Proposal Date</p>
              <p className="text-lg font-semibold">{new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="mr-2" size={20} />
              Customer Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={customer.name}
                  onChange={(e) => handleCustomerChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter contact person"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={customer.address}
                  onChange={(e) => handleCustomerChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter full address"
                />
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={customer.email}
                    onChange={(e) => handleCustomerChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={customer.phone}
                    onChange={(e) => handleCustomerChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Overview */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Building className="mr-2" size={20} />
            Project Overview
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Server className="text-blue-600 mr-2" size={20} />
                <span className="font-medium text-blue-900">Equipment</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{totalEquipment}</p>
              <p className="text-sm text-blue-700">Network devices</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Cable className="text-green-600 mr-2" size={20} />
                <span className="font-medium text-green-900">Cables</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{totalCables}</p>
              <p className="text-sm text-green-700">Network connections</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <Wifi className="text-purple-600 mr-2" size={20} />
                <span className="font-medium text-purple-900">Coverage</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">100%</p>
              <p className="text-sm text-purple-700">Network coverage</p>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Description</h3>
            <p className="text-gray-700 leading-relaxed">
              This proposal outlines the design and implementation of a comprehensive network infrastructure 
              for {customer.name || 'your organization'}. The solution includes enterprise-grade networking 
              equipment, structured cabling, and wireless access points to provide reliable, high-performance 
              connectivity throughout the facility.
            </p>
          </div>
        </div>

        {/* Scope of Work */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CheckCircle className="mr-2" size={20} />
            Scope of Work
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Network Design</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Network topology design and documentation
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Equipment selection and specification
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Cable routing and management planning
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Wireless coverage analysis
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Implementation</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Equipment installation and configuration
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Structured cabling installation
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Network testing and validation
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckCircle className="text-green-500 mr-2" size={16} />
                  Documentation and handover
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Calendar className="mr-2" size={20} />
            Project Timeline
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mr-4">1</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Planning & Design</h3>
                <p className="text-gray-600">Network design and equipment selection</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold">3-5 days</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-green-50 rounded-lg">
              <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mr-4">2</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Equipment Procurement</h3>
                <p className="text-gray-600">Ordering and delivery of network equipment</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold">7-14 days</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-purple-50 rounded-lg">
              <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mr-4">3</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Installation</h3>
                <p className="text-gray-600">Physical installation and cabling</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold">5-10 days</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-orange-50 rounded-lg">
              <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mr-4">4</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Testing & Handover</h3>
                <p className="text-gray-600">Network testing and documentation</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-semibold">2-3 days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DollarSign className="mr-2" size={20} />
            Investment Summary
          </h2>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    All network equipment and hardware
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    Structured cabling and patch panels
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    Professional installation and configuration
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    Network testing and validation
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    Complete documentation package
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    12-month warranty on all equipment
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Equipment & Materials</span>
                    <span className="font-semibold">{formatCurrencyNoDecimals(0, currency)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Installation & Labor</span>
                    <span className="font-semibold">{formatCurrencyNoDecimals(0, currency)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Testing & Documentation</span>
                    <span className="font-semibold">{formatCurrencyNoDecimals(0, currency)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 bg-blue-50 rounded-lg px-4">
                    <span className="text-lg font-semibold text-gray-900">Total Investment</span>
                    <span className="text-xl font-bold text-blue-900">{formatCurrencyNoDecimals(0, currency)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Clock className="mr-2" size={20} />
            Next Steps
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start p-4 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">1</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Review & Approve Proposal</h3>
                <p className="text-gray-600">Please review this proposal and approve the scope of work and investment.</p>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-green-50 rounded-lg">
              <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">2</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Sign Contract</h3>
                <p className="text-gray-600">Once approved, we'll prepare the contract for signature.</p>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-purple-50 rounded-lg">
              <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">3</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Project Kickoff</h3>
                <p className="text-gray-600">We'll schedule the project kickoff meeting and begin implementation.</p>
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Phone className="text-gray-500 mr-2" size={16} />
                <span className="text-gray-700">(555) 123-4567</span>
              </div>
              <div className="flex items-center">
                <Mail className="text-gray-500 mr-2" size={16} />
                <span className="text-gray-700">info@networkpro.com</span>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalView;
